
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle01Icon } from "@hugeicons/core-free-icons";
import { AnimatedText } from "../ui/animated-text";
import { AnimatedImage } from "../ui/animated-image";

interface FeatureRowProps {
    title: string;
    subtitle?: string;
    description: string;
    imageSrc: string;
    features: string[];
    isReversed?: boolean;
}

export function FeatureRow({ title, subtitle, description, imageSrc, features, isReversed = false }: FeatureRowProps) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section id="features" ref={ref} className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
            <div className="container mx-auto max-w-6xl">
                <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 sm:gap-12 lg:gap-24`}>

                    {/* Text Content */}
                    <motion.div
                        initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="flex-1"
                    >
                        <div className="mb-4 sm:mb-6">
                            <div className="flex items-center gap-3 mb-2">
                                <AnimatedText
                                    lines={[title]}
                                    className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-balance"
                                    direction="up"
                                    as="h2"
                                />
                            </div>
                        </div>
                        {subtitle && <p className="text-lg sm:text-xl text-gray-500 mb-4 sm:mb-6 text-balance">{subtitle}</p>}
                        <p className="text-base sm:text-lg text-gray-400 mb-6 sm:mb-8 leading-relaxed text-balance">
                            {description}
                        </p>

                        <ul className="space-y-3 sm:space-y-4">
                            {features.map((feature, index) => (
                                <li key={index} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={12} className="sm:w-4 sm:h-4" />
                                    </div>
                                    <span className="text-sm sm:text-base text-gray-300 font-medium">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Image Content */}
                    <div className="flex-1 w-full">
                        <div className="relative aspect-square md:aspect-[4/3] rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-900 border border-white/10 group">
                            <AnimatedImage
                                src={imageSrc}
                                alt={title}
                                className="w-full h-full hover:scale-105 transition-transform duration-700"
                                delay={0.2}
                            />
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 text-center mt-4 sm:mt-6 italic">
                            Das fertige Produkt kann leicht von der Darstellung abweichen
                        </p>
                    </div>

                </div>
            </div>
        </section>
    );
}
